import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Activity, Monitor, Smartphone, AlertTriangle, Loader2, MapPin, Globe2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Session Audit Log card. Shows up to 20 recent sessions with IP + geo + device,
 * flags the current session, and offers a "Sign out all other sessions" action
 * that cryptographically invalidates every other JWT via pwv bump.
 */
export default function SessionAuditCard({ token, setToken }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/sessions`, { headers });
      setSessions(res.data.sessions || []);
    } catch (e) {
      if (e.response?.status !== 401) {
        toast.error('Could not load sessions');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const revokeOthers = async () => {
    if (!confirm('Sign out of all other devices? You will stay logged in here.')) return;
    setRevoking(true);
    try {
      const res = await axios.post(`${API}/auth/sessions/revoke-others`, {}, { headers });
      if (res.data?.access_token) {
        localStorage.setItem('kstreak_token', res.data.access_token);
        if (setToken) setToken(res.data.access_token);
      }
      await load();
      toast.success(`${res.data.revoked} other session${res.data.revoked === 1 ? '' : 's'} signed out`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not revoke sessions');
    } finally { setRevoking(false); }
  };

  const others = sessions.filter(s => !s.is_current && !s.revoked_at);
  const currentCount = sessions.filter(s => s.is_current).length;

  const relativeTime = (iso) => {
    if (!iso) return '—';
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
  };

  const methodLabel = {
    password: 'Password',
    'password+2fa': 'Password + 2FA',
    google: 'Google SSO',
    register: 'Signup',
  };

  return (
    <Card className="border-[#E5E7EB] mb-6" data-testid="session-audit-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#002FA7]" />
            Active Sessions
            <span className="text-xs font-normal text-[#9CA3AF]">(last 20)</span>
          </CardTitle>
          {others.length > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={revokeOthers}
              disabled={revoking}
              data-testid="revoke-others-btn"
            >
              {revoking ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
              Sign out {others.length} other{others.length === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#4B5563] mb-4">
          Every time you log in, we record the device and location. If you see something unfamiliar below,
          click <strong>Sign out others</strong> immediately — this cryptographically invalidates every other
          login token (not just marks them revoked).
        </p>

        {loading && sessions.length === 0 ? (
          <div className="text-center py-6">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#9CA3AF]" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-[#9CA3AF] italic">No session history yet.</p>
        ) : (
          <div className="space-y-2" data-testid="sessions-list">
            {sessions.map(s => (
              <div
                key={s.session_id}
                className={`p-3 border rounded ${s.is_current ? 'border-[#002FA7] bg-[#002FA7]/5' : s.revoked_at ? 'border-[#E5E7EB] bg-[#FAFAFA] opacity-60' : 'border-[#E5E7EB]'}`}
                data-testid={`session-row-${s.session_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                    {s.device === 'Mobile'
                      ? <Smartphone className="w-4 h-4 text-[#4B5563]" />
                      : <Monitor className="w-4 h-4 text-[#4B5563]" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-medium text-[#0A0A0A]">
                        {s.browser} on {s.os}
                      </span>
                      {s.is_current && (
                        <Badge className="text-[10px] bg-[#002FA7]/15 text-[#002FA7] border-0" data-testid="current-session-badge">
                          This device
                        </Badge>
                      )}
                      {s.revoked_at && (
                        <Badge variant="outline" className="text-[10px] text-[#9CA3AF]">
                          Revoked
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        {methodLabel[s.method] || s.method}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#4B5563] flex-wrap">
                      {(s.city || s.country) && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {[s.city, s.region, s.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {s.ip && (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Globe2 className="w-3 h-3" />
                          {s.ip}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] mt-1">
                      Signed in {relativeTime(s.created_at)}
                      {s.isp ? ` · ${s.isp}` : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {currentCount === 0 && sessions.length > 0 && (
          <p className="text-xs text-[#9CA3AF] italic mt-2">
            (Your current session was issued before session auditing was enabled — it won't be flagged, but the next login will be tracked.)
          </p>
        )}
      </CardContent>
    </Card>
  );
}
